import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SystemLockService } from '../../../core/portal/lock-system.service';
import { Subscription, interval } from 'rxjs';
import Swal from 'sweetalert2';
import { BlockUI, NgBlockUI } from 'ng-block-ui';

@Component({
  selector: 'app-system-lock',
  templateUrl: './system-lock.component.html',
  styleUrls: ['./system-lock.component.css']
})
export class SystemLockComponent implements OnInit, OnDestroy {
  isLocked = false;
  expiryTime: Date | null = null;
  countdownText = '';
  duration = 60;

  // Track which groups should be affected by the lock
  selectedScope = {
    Doctor: true,
    center: true,
    admin: false
  };

  @BlockUI() blockUI: NgBlockUI;
  private timerSubscription?: Subscription;

  constructor(
    private lockService: SystemLockService,
    private changeDetect: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.checkStatus();
  }

  /**
   * Fetches current maintenance status from the PHP backend
   */
  checkStatus() {
    this.lockService.getLockStatus().subscribe(res => {
      this.isLocked = res.isLocked;
      if (res.isLocked && res.expiry) {
        // Parse PHP date string (YYYY-MM-DD HH:MM:SS) safely
        this.expiryTime = new Date(res.expiry.replace(/-/g, "/"));
        this.startCountdown();
      } else {
        this.stopCountdown();
      }
      this.safeDetectChanges();
    });
  }

  /**
   * Triggers the lock or unlock process with a confirmation dialog
   */
  toggleLock() {
    const title = this.isLocked ? 'Unlock System?' : 'Lock System?';

    // Create an empty array to hold the selected groups
    const scopeArray: string[] = [];

    // Manually check each property of the object
    // This is the most stable way to avoid 'this' context errors in TS
    if (this.selectedScope.Doctor) { scopeArray.push('Doctor'); }
    if (this.selectedScope.center) { scopeArray.push('center'); }
    if (this.selectedScope.admin) { scopeArray.push('admin'); }

    if (!this.isLocked && scopeArray.length === 0) {
      Swal.fire('Selection Required', 'Please select at least one user group to lock out.', 'info');
      return;
    }

    const text = this.isLocked
      ? 'This will restore access for all users immediately.'
      : `This will log out and block access for: ${scopeArray.join(', ')} for ${this.duration} minutes.`;

    Swal.fire({
      title: title,
      text: text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, proceed',
      cancelButtonText: 'Cancel',
      confirmButtonColor: this.isLocked ? '#28a745' : '#dc3545'
    }).then((result) => {
      // Inside the SweetAlert promise, we use result.value
      if (result.value) {
        this.blockUI.start(this.isLocked ? 'Restoring access...' : 'Locking system...');
        const targetStatus = this.isLocked ? 0 : 1;

        // Send to service
        this.lockService.updateLockStatus(targetStatus, this.duration, scopeArray).subscribe(() => {
          this.checkStatus();
          this.blockUI.stop();
          Swal.fire('Success', 'System access rules updated.', 'success');
          this.safeDetectChanges();
        }, error => {
          this.blockUI.stop();
          const errorMessage = (error.error && error.error.message) ? error.error.message : 'Server error';
          Swal.fire('Error', errorMessage , 'error');
        });
      }
    });
  }

  startCountdown() {
    this.stopCountdown();
    this.timerSubscription = interval(1000).subscribe(() => {
      this.updateCountdownText();
    });
  }

  updateCountdownText() {
    if (!this.expiryTime) return;

    const now = new Date().getTime();
    const distance = this.expiryTime.getTime() - now;

    if (distance <= 0) {
      this.countdownText = "Maintenance Expired (System remains locked)";
      this.stopCountdown();
    } else {
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      this.countdownText = `${hours}h ${minutes}m ${seconds}s remaining`;
    }
    this.safeDetectChanges();
  }

  stopCountdown() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = undefined;
    }
    this.countdownText = '';
    this.safeDetectChanges();
  }

  /**
   * Helper to prevent "ChangeDetectorRef.detectChanges" errors 
   * when navigating away or during component destruction
   */
  private safeDetectChanges() {
    if (!(this.changeDetect as any).destroyed) {
      this.changeDetect.detectChanges();
    }
  }

  ngOnDestroy() {
    // Clean up timer to prevent memory leaks and background errors
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }
}